import React, { useState } from 'react';
import { CourseV2 } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';
import { ClockIcon, UserIcon, ChevronRightIcon, PlayIcon, BookOpenIcon } from './icons';

interface CourseV2BentoGridProps {
  courses: CourseV2[];
}

const CourseV2BentoGrid: React.FC<CourseV2BentoGridProps> = ({ courses }) => {
  const { colors } = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  
  // Take first 3 courses for featured display
  const featuredCourses = courses.slice(0, 3);
  const additionalCourses = courses.slice(3, showAll ? courses.length : 6);

  return (
    <div className="space-y-8">
      {/* Bento Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[300px]">
        {featuredCourses.map((course, index) => (
          <Link
            key={course._id}
            to={`/coursesv2/${course._id}`}
            className={`
              relative overflow-hidden rounded-2xl transition-all duration-500 group
              ${index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}
              animate-fadeInScale stagger-${index + 1}
            `}
            style={{
              backgroundColor: colors.background.paper,
              boxShadow: hoveredIndex === index 
                ? `0 20px 40px -10px ${colors.accent.mint}40` 
                : `0 10px 30px -10px ${colors.accent.mint}20`,
              transform: hoveredIndex === index ? 'translateY(-4px)' : 'translateY(0)'
            }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {/* Background Image */}
            <div 
              className="absolute inset-0 transition-transform duration-700 group-hover:scale-110"
              style={{
                backgroundImage: `url(${course.image || 'https://picsum.photos/seed/course/800/600'})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            
            {/* Gradient Overlay */}
            <div 
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to bottom, transparent 0%, ${colors.background.paper}dd 70%, ${colors.background.paper} 100%)`
              }}
            />

            {/* Content */}
            <div className="absolute inset-0 p-6 flex flex-col justify-end">
              {index === 0 && (
                <div className="mb-auto">
                  <span 
                    className="inline-block px-4 py-2 rounded-full text-white text-sm font-semibold"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
                    }}
                  >
                    FEATURED
                  </span>
                </div>
              )}
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span 
                    className="text-sm font-semibold px-3 py-1 rounded-full"
                    style={{ 
                      backgroundColor: colors.accent.warmYellow,
                      color: colors.primary.contrastText
                    }}
                  >
                    {course.examType}
                  </span>
                  <span 
                    className="text-sm font-medium px-3 py-1 rounded-full"
                    style={{ 
                      backgroundColor: `${colors.accent.mint}20`,
                      color: colors.accent.mint
                    }}
                  >
                    {course.mode}
                  </span>
                </div>
                
                <h3 
                  className={`font-bold leading-tight ${index === 0 ? 'text-2xl md:text-3xl' : 'text-xl'}`}
                  style={{ color: colors.text.primary }}
                >
                  {course.title}
                </h3>
                
                {index === 0 && (
                  <p 
                    className="text-sm md:text-base line-clamp-2"
                    style={{ color: colors.text.secondary }}
                  >
                    {course.description}
                  </p>
                )}
                
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <UserIcon className="w-4 h-4" style={{ color: colors.text.secondary }} />
                    <span style={{ color: colors.text.secondary }}>
                      {course.instructor && typeof course.instructor === 'object' ? course.instructor.name : 'Expert Instructor'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <BookOpenIcon className="w-4 h-4" style={{ color: colors.text.secondary }} />
                    <span style={{ color: colors.text.secondary }}>{course.subjects.length} Subjects</span>
                  </div>
                </div>
                
                {index === 0 && (
                  <div className="flex items-center justify-end pt-2">
                    <div 
                      className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ color: colors.accent.mint }}
                    >
                      <PlayIcon className="w-5 h-5" />
                      <span className="text-sm font-medium">View Details</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Hover Effect Border */}
            <div 
              className="absolute inset-0 rounded-2xl border-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{ borderColor: colors.accent.mint }}
            />
          </Link>
        ))}
      </div>

      {/* Additional Courses (Expandable) */}
      {additionalCourses.length > 0 && (
        <div 
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 ${
            showAll ? 'opacity-100 max-h-[1000px]' : 'opacity-0 max-h-0 overflow-hidden'
          }`}
        >
          {additionalCourses.map((course, index) => (
            <Link
              key={course._id}
              to={`/coursesv2/${course._id}`}
              className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.02] group"
              style={{
                backgroundColor: colors.background.paper,
                boxShadow: `0 10px 30px -10px ${colors.accent.mint}20`
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span 
                  className="text-xs font-semibold px-2 py-1 rounded-full"
                  style={{ 
                    backgroundColor: colors.accent.warmYellow,
                    color: colors.primary.contrastText
                  }}
                >
                  {course.examType}
                </span>
              </div>
              <h4 
                className="font-semibold text-lg mb-2 group-hover:text-accent-mint transition-colors"
                style={{ color: colors.text.primary }}
              >
                {course.title}
              </h4>
              <p className="text-sm mb-3" style={{ color: colors.text.secondary }}>
                {course.subjects.join(', ')} • {course.mode}
              </p>
              <div className="flex items-center justify-end">
                <ChevronRightIcon 
                  className="w-5 h-5 transition-transform group-hover:translate-x-1" 
                  style={{ color: colors.accent.mint }}
                />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* View More/Less Button */}
      {courses.length > 3 && (
        <div className="text-center">
          <button
            onClick={(e) => {
              e.preventDefault();
              setShowAll(!showAll);
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: `${colors.accent.mint}10`,
              color: colors.accent.mint
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${colors.accent.mint}20`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = `${colors.accent.mint}10`;
            }}
          >
            {showAll ? 'Show Less' : `View ${courses.length - 3} More Courses`}
            <ChevronRightIcon 
              className={`w-5 h-5 transition-transform ${showAll ? 'rotate-90' : ''}`}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default CourseV2BentoGrid;